/**
 * HailProtector PDP Main - v7.31.3 (Modular)
 * Slim orchestrator that ties together modular components
 * 
 * Dependencies (load in order):
 * 1. pdp-vehicle-data.js - YMM mappings
 * 2. pdp-gallery.js - Gallery component
 * 3. pdp-variants.js - Variant integration
 * 4. pdp-ui.js - UI components
 * 5. pdp-hailprotector.js - This file (orchestrator)
 * 
 * v7.31.3 Changes:
 * - Removed auto-add 2-Year Warranty on kit purchase
 * 
 * v7.31.2 Changes:
 * - Fixed gallery selector: [data-gallery] → [data-gallery-component]
 * 
 * v7.31.1 Changes:
 * - Added Finder Toggle initialization (Find your fit panel)
 * - Added Size Guide Modal initialization
 * - Added Apply Size button handler for YMM selection
 * 
 * v7.31.0 Changes:
 * - MODULAR: Split from 800+ lines to slim orchestrator (~200 lines)
 * - Extracted vehicle data to pdp-vehicle-data.js
 * - Extracted gallery logic to pdp-gallery.js
 * - Extracted variant handling to pdp-variants.js
 * - Extracted UI components to pdp-ui.js
 * - 100% functionality retained
 */

document.addEventListener('DOMContentLoaded', function() {
  'use strict';
  
  var pdp = document.querySelector('.hp-pdp');
  if (!pdp) return;
  
  var HP = window.HailProtector;
  if (!HP) {
    console.error('HailProtector modules not loaded');
    return;
  }
  
  // ========================================
  // STATE
  // ========================================
  var activeSystem = pdp.dataset.activeSystem || 'sentinel';
  var selectedSize = null;
  var selectedColor = pdp.dataset.activeColor || 'silver';
  var BLACK_PRICE_PREMIUM = 50;
  
  // ========================================
  // INITIALIZE COMPONENTS
  // ========================================
  
  // Gallery (v7.31.2: fixed selector to match template)
  var galleryContainer = pdp.querySelector('[data-gallery-component]');
  var gallery = HP.Gallery ? HP.Gallery.create(galleryContainer) : null;
  
  // Variants
  var variants = HP.Variants ? HP.Variants.create(pdp, {
    onChange: handleVariantChange,
    onHistoryChange: handleHistoryChange
  }) : null;
  
  // UI Components
  var UI = HP.UI || {};
  
  // Accordions
  UI.initAccordions && UI.initAccordions(pdp);
  
  // Sticky CTA
  UI.initStickyCTA && UI.initStickyCTA(pdp);
  
  // YMM Selector
  var ymmSelector = UI.initYMMSelector ? UI.initYMMSelector(pdp, {
    onSelect: handleYMMSelect
  }) : null;
  
  // Finder Toggle (v7.31.1) - "Not sure which size? Find your fit"
  var finderToggle = UI.initFinderToggle ? UI.initFinderToggle(pdp) : null;
  
  // Size Guide Modal (v7.31.1)
  var sizeGuideModal = UI.initSizeGuideModal ? UI.initSizeGuideModal(pdp) : null;
  
  // Size Selector
  var sizeSelector = UI.initSizeSelector ? UI.initSizeSelector(pdp, {
    onSelect: handleSizeSelect
  }) : null;
  
  // Color Selector
  var colorSelector = UI.initColorSelector ? UI.initColorSelector(pdp, {
    onSelect: handleColorSelect
  }) : null;
  
  // Price Display
  var priceDisplay = UI.initPriceDisplay ? UI.initPriceDisplay(pdp) : null;
  
  // Add to Cart
  var addToCart = UI.initAddToCart ? UI.initAddToCart(pdp) : null;
  
  // ========================================
  // DOM REFERENCES (for direct manipulation)
  // ========================================
  var toggleBtns = pdp.querySelectorAll('[data-system]');
  var systemTitles = pdp.querySelectorAll('[data-system-title]');
  var systemSubtitles = pdp.querySelectorAll('[data-system-subtitle]');
  var systemSizes = pdp.querySelectorAll('[data-system-sizes]');
  var systemLabels = pdp.querySelectorAll('[data-system-label]');
  var systemPrices = pdp.querySelectorAll('[data-system-price]');
  var sizeBtns = pdp.querySelectorAll('[data-size]');
  var colorBtns = pdp.querySelectorAll('[data-color]');
  var priceNote = pdp.querySelector('[data-price-note]');
  var modelNumberDisplay = pdp.querySelector('[data-model-number]');
  var stickyTitle = pdp.querySelector('[data-sticky-title]');
  var stickyPrice = pdp.querySelector('[data-sticky-price]');
  var stickyBtn = pdp.querySelector('[data-sticky-add]');
  var addBtn = pdp.querySelector('[data-add-to-cart]');

  // ========================================
  // AVAILABILITY (stock-aware · continue-selling aware)
  //
  // Single source of truth is variant.available from the product JSON.
  // Shopify sets available=false ONLY when inventory is tracked AND the
  // variant policy is DENY AND quantity <= 0. A variant set to
  // "continue selling when out of stock" (policy CONTINUE) stays
  // available=true even at 0 on-hand, so it will NOT show an OOS state —
  // exactly the overstock-aware behavior we want. No inventory-number
  // math here; we only read `available`.
  // ========================================
  var allVariants = (variants && variants.getProductData() && variants.getProductData().variants) || [];

  function variantFor(size, color) {
    for (var i = 0; i < allVariants.length; i++) {
      var v = allVariants[i];
      var vSize = v.option1 || '';
      var vColor = (v.option2 || 'silver').toLowerCase();
      if (size && vSize.indexOf(size) !== -1 && vColor === color) return v;
    }
    return null;
  }

  // A size+color is buyable only if its variant EXISTS and is available.
  // A missing variant (e.g. deleted) is treated as unavailable — never addable.
  // If we have NO variant data at all (JSON parse failed), fail OPEN so a
  // data glitch never blocks every sale.
  function isAvailable(size, color) {
    if (!allVariants.length) return true;
    var v = variantFor(size, color);
    return !!(v && v.available);
  }

  // A color is buyable if ANY size in that color is available. Fail open on no data.
  function colorHasStock(color) {
    if (!allVariants.length) return true;
    for (var i = 0; i < allVariants.length; i++) {
      var v = allVariants[i];
      var vColor = (v.option2 || 'silver').toLowerCase();
      if (vColor === color && v.available) return true;
    }
    return false;
  }

  // Grey out / disable color swatches whose entire color is sold out.
  function refreshColorAvailability() {
    for (var i = 0; i < colorBtns.length; i++) {
      var btn = colorBtns[i];
      var oos = !colorHasStock(btn.dataset.color);
      btn.classList.toggle('hp-pdp__color-btn--oos', oos);
      btn.setAttribute('aria-disabled', oos ? 'true' : 'false');
    }
  }

  // Grey out / disable size buttons sold out IN THE CURRENTLY SELECTED COLOR.
  function refreshSizeAvailability() {
    for (var i = 0; i < sizeBtns.length; i++) {
      var btn = sizeBtns[i];
      var oos = !isAvailable(btn.dataset.size, selectedColor);
      btn.classList.toggle('hp-pdp__size-btn--oos', oos);
      btn.setAttribute('aria-disabled', oos ? 'true' : 'false');
    }
  }

  // If a size is sold out in the current color but available in the other,
  // switch to the available color (used by YMM apply, where color is implicit).
  function ensureAvailableColorFor(size) {
    if (isAvailable(size, selectedColor)) return;
    var alt = selectedColor === 'black' ? 'silver' : 'black';
    if (isAvailable(size, alt)) {
      selectedColor = alt;
      pdp.dataset.activeColor = alt;
      updateColorUI();
    }
  }

  // ========================================
  // HANDLERS
  // ========================================
  
  function handleVariantChange(variant) {
    if (!variant) return;
    
    // Update model number
    updateModelNumber(variant.sku);
    
    // Update add to cart button
    if (addToCart) {
      addToCart.updateButton(variant);
    }
  }
  
  function handleHistoryChange(variant) {
    if (!variant) return;
    
    var parsed = variants.parseVariant(variant);
    if (!parsed) return;
    
    // Restore UI state
    if (parsed.system !== activeSystem) {
      setActiveSystem(parsed.system, false);
    }
    if (parsed.color !== selectedColor) {
      selectedColor = parsed.color;
      updateColorUI();
    }
    if (parsed.size !== selectedSize) {
      selectSize(parsed.size, false);
    }
    
    // Update gallery
    if (gallery) {
      gallery.filter(parsed.system, parsed.color, variant);
    }
  }
  
  function handleYMMSelect(sizeCode, make, model) {
    var system = HP.sizeToSystem ? HP.sizeToSystem(sizeCode) : 
      (sizeCode.indexOf('CAR') === 0 ? 'sentinel' : 'guardian');
    
    setActiveSystem(system);
    // If their fitted size is sold out in the current color but available
    // in the other, switch color so the recommendation stays buyable.
    ensureAvailableColorFor(sizeCode);
    selectSize(sizeCode);
  }
  
  function handleSizeSelect(size) {
    selectSize(size);
  }
  
  function handleColorSelect(color) {
    selectedColor = color;
    pdp.dataset.activeColor = color;
    
    updateSizePricesForColor(color);
    
    if (selectedSize) {
      updateVariant(selectedSize, color);
    }
    
    if (gallery) {
      gallery.filter(activeSystem, color);
    }
  }
  
  // ========================================
  // SYSTEM TOGGLE
  // ========================================
  
  function setActiveSystem(system, triggerUpdate) {
    if (triggerUpdate === undefined) triggerUpdate = true;
    
    activeSystem = system;
    pdp.dataset.activeSystem = system;
    
    // Update toggle buttons
    for (var i = 0; i < toggleBtns.length; i++) {
      toggleBtns[i].classList.toggle('active', toggleBtns[i].dataset.system === system);
    }
    
    // Update visibility
    updateSystemVisibility(systemTitles, 'systemTitle', system);
    updateSystemVisibility(systemSubtitles, 'systemSubtitle', system);
    updateSystemVisibility(systemSizes, 'systemSizes', system);
    updateSystemVisibility(systemLabels, 'systemLabel', system);
    updateSystemVisibility(systemPrices, 'systemPrice', system);
    
    // Reset size
    selectedSize = null;
    for (var j = 0; j < sizeBtns.length; j++) {
      sizeBtns[j].classList.remove('selected');
    }
    updatePriceDisplay(null, null);
    updateModelNumber(null);
    
    // Filter gallery
    if (gallery) {
      gallery.filter(system, selectedColor);
    }

    // Reflect stock state on the newly-visible size group
    refreshSizeAvailability();
  }
  
  function updateSystemVisibility(elements, dataAttr, system) {
    for (var i = 0; i < elements.length; i++) {
      elements[i].classList.toggle('hidden', elements[i].dataset[dataAttr] !== system);
    }
  }
  
  // Bind system toggles
  for (var i = 0; i < toggleBtns.length; i++) {
    (function(btn) {
      btn.addEventListener('click', function() {
        setActiveSystem(btn.dataset.system);
      });
    })(toggleBtns[i]);
  }
  
  // ========================================
  // SIZE SELECTION
  // ========================================
  
  function selectSize(size, triggerUpdate) {
    if (triggerUpdate === undefined) triggerUpdate = true;
    
    selectedSize = size;
    
    for (var i = 0; i < sizeBtns.length; i++) {
      sizeBtns[i].classList.toggle('selected', sizeBtns[i].dataset.size === size);
    }
    
    var btn = pdp.querySelector('[data-size="' + size + '"]');
    var basePrice = btn && btn.dataset.price ? parseInt(btn.dataset.price, 10) : null;
    
    updatePriceDisplay(basePrice, size);
    
    if (triggerUpdate) {
      updateVariant(size, selectedColor);
    }
  }
  
  // Bind size buttons
  for (var j = 0; j < sizeBtns.length; j++) {
    (function(btn) {
      btn.addEventListener('click', function() {
        // Block selecting a size sold out in the current color.
        if (!isAvailable(btn.dataset.size, selectedColor)) return;
        selectSize(btn.dataset.size);
      });
    })(sizeBtns[j]);
  }
  
  // ========================================
  // COLOR SELECTION
  // ========================================
  
  function updateColorUI() {
    for (var i = 0; i < colorBtns.length; i++) {
      colorBtns[i].classList.toggle('selected', colorBtns[i].dataset.color === selectedColor);
    }
    updateSizePricesForColor(selectedColor);
    refreshColorAvailability();
    refreshSizeAvailability();
  }
  
  function updateSizePricesForColor(color) {
    for (var i = 0; i < sizeBtns.length; i++) {
      var btn = sizeBtns[i];
      var basePrice = parseInt(btn.dataset.price, 10);
      var displayPrice = color === 'black' ? basePrice + BLACK_PRICE_PREMIUM : basePrice;
      var priceEl = btn.querySelector('.hp-pdp__size-price');
      if (priceEl) {
        priceEl.textContent = '$' + displayPrice;
      }
    }
  }
  
  // Bind color buttons
  for (var k = 0; k < colorBtns.length; k++) {
    (function(btn) {
      btn.addEventListener('click', function() {
        // Block selecting a fully sold-out color.
        if (!colorHasStock(btn.dataset.color)) return;
        selectedColor = btn.dataset.color;
        pdp.dataset.activeColor = selectedColor;
        updateColorUI();

        if (selectedSize) {
          var baseBtn = pdp.querySelector('[data-size="' + selectedSize + '"]');
          var basePrice = baseBtn ? parseInt(baseBtn.dataset.price, 10) : null;
          updatePriceDisplay(basePrice, selectedSize);
          updateVariant(selectedSize, selectedColor);
        }
        
        if (gallery) {
          gallery.filter(activeSystem, selectedColor);
        }
      });
    })(colorBtns[k]);
  }
  
  // ========================================
  // VARIANT UPDATE
  // ========================================
  
  function updateVariant(size, color) {
    if (!size || !variants) return;
    
    var variant = variants.select(size, color);
    
    if (variant && gallery) {
      gallery.filter(activeSystem, color, variant);
    }
  }
  
  // ========================================
  // DISPLAY UPDATES
  // ========================================
  
  function updatePriceDisplay(basePrice, size) {
    var sizeNames = HP.sizeNames || {};
    
    if (basePrice && size) {
      var finalPrice = selectedColor === 'black' ? basePrice + BLACK_PRICE_PREMIUM : basePrice;
      
      if (priceNote) {
        priceNote.textContent = sizeNames[size] || size;
        priceNote.style.color = 'var(--hp-text)';
        priceNote.style.fontWeight = '500';
      }
      if (stickyTitle) stickyTitle.textContent = sizeNames[size] || size;
      if (stickyPrice) stickyPrice.textContent = '$' + finalPrice;
      if (stickyBtn) stickyBtn.disabled = !isAvailable(size, selectedColor);
    } else {
      if (priceNote) {
        priceNote.textContent = 'Select size below';
        priceNote.style.color = '';
        priceNote.style.fontWeight = '';
      }
      if (stickyTitle) stickyTitle.textContent = 'Select a size';
      if (stickyBtn) stickyBtn.disabled = true;
    }
  }
  
  function updateModelNumber(sku) {
    if (modelNumberDisplay) {
      modelNumberDisplay.textContent = sku || '—';
    }
  }
  
  // ========================================
  // QUANTITY CONTROLS
  // ========================================
  var qtyInput = pdp.querySelector('[data-quantity]');
  var minusBtn = pdp.querySelector('[data-qty-minus]');
  var plusBtn = pdp.querySelector('[data-qty-plus]');
  
  if (minusBtn) {
    minusBtn.addEventListener('click', function() {
      if (qtyInput && qtyInput.value > 1) qtyInput.value--;
    });
  }
  
  if (plusBtn) {
    plusBtn.addEventListener('click', function() {
      if (qtyInput) qtyInput.value++;
    });
  }
  
  // ========================================
  // APPLY SIZE BUTTON (v7.31.1)
  // ========================================
  var applySizeBtn = pdp.querySelector('[data-apply-size]');
  
  if (applySizeBtn && ymmSelector) {
    applySizeBtn.addEventListener('click', function() {
      var sizeResult = ymmSelector.getSelectedSize();
      
      // getSelectedSize returns a Promise
      var handleSize = function(size) {
        if (size) {
          // Determine system from size code
          var system = HP.sizeToSystem ? HP.sizeToSystem(size) : 
            (size.indexOf('CAR') === 0 ? 'sentinel' : 'guardian');
          
          // Set system and size
          setActiveSystem(system);
          ensureAvailableColorFor(size);
          selectSize(size);
          
          // Close finder panel
          if (finderToggle) {
            finderToggle.close();
          }
        
          // Scroll to purchase card
          var purchaseCard = pdp.querySelector('.hp-pdp__purchase-card');
          if (purchaseCard) {
            purchaseCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      };

      // Handle both Promise and direct value returns
      if (sizeResult && typeof sizeResult.then === 'function') {
        sizeResult.then(handleSize);
      } else {
        handleSize(sizeResult);
      }
    });
  }
  
  // ========================================
  // ACCORDION: DETAILS
  // ========================================
  var detailToggles = pdp.querySelectorAll('[data-detail-toggle]');
  
  for (var d = 0; d < detailToggles.length; d++) {
    (function(toggle) {
      toggle.addEventListener('click', function() {
        var id = toggle.dataset.detailToggle;
        var content = pdp.querySelector('[data-detail-content="' + id + '"]');
        var isOpen = toggle.classList.contains('open');
        
        // Close others
        for (var i = 0; i < detailToggles.length; i++) {
          if (detailToggles[i] !== toggle) {
            var otherId = detailToggles[i].dataset.detailToggle;
            var otherContent = pdp.querySelector('[data-detail-content="' + otherId + '"]');
            detailToggles[i].classList.remove('open');
            if (otherContent) otherContent.classList.add('hidden');
          }
        }
        
        toggle.classList.toggle('open', !isOpen);
        if (content) content.classList.toggle('hidden', isOpen);
      });
    })(detailToggles[d]);
  }
  
  // ========================================
  // ACCORDION: FAQ
  // ========================================
  var faqToggles = pdp.querySelectorAll('[data-faq-toggle]');
  
  for (var f = 0; f < faqToggles.length; f++) {
    (function(toggle) {
      toggle.addEventListener('click', function() {
        var item = toggle.closest('.hp-pdp__faq-item');
        var answer = item ? item.querySelector('.hp-pdp__faq-answer') : null;
        var isOpen = item && item.classList.contains('open');
        
        // Close all
        var allItems = pdp.querySelectorAll('.hp-pdp__faq-item');
        for (var i = 0; i < allItems.length; i++) {
          allItems[i].classList.remove('open');
          var ans = allItems[i].querySelector('.hp-pdp__faq-answer');
          if (ans) ans.classList.add('hidden');
        }
        
        if (!isOpen && item && answer) {
          item.classList.add('open');
          answer.classList.remove('hidden');
        }
      });
    })(faqToggles[f]);
  }
  
  // ========================================
  // STICKY CTA
  // ========================================
  var stickyCta = pdp.querySelector('[data-sticky-cta]');
  var purchaseCard = pdp.querySelector('.hp-pdp__purchase-card');
  
  if (stickyCta && purchaseCard) {
    window.addEventListener('scroll', function() {
      stickyCta.classList.toggle('visible', purchaseCard.getBoundingClientRect().bottom < 0);
    }, { passive: true });
    
    if (stickyBtn && addBtn) {
      stickyBtn.addEventListener('click', function() {
        addBtn.click();
      });
    }
  }
  
  // ========================================
  // SCROLL TO TOP
  // ========================================
  var scrollTopBtn = pdp.querySelector('[data-scroll-to-top]');
  
  if (scrollTopBtn) {
    scrollTopBtn.addEventListener('click', function() {
      var hero = pdp.querySelector('.hp-pdp__hero');
      if (hero) hero.scrollIntoView({ behavior: 'smooth' });
    });
  }
  
  // ========================================
  // FORM SUBMISSION
  // ========================================
  var form = pdp.querySelector('[data-product-form]');
  
  // Vehicle nudge state
  var vehicleNudgeSkipped = false;
  var nudgeOverlay = pdp.querySelector('[data-vehicle-nudge]');
  var nudgeCloseBtn = nudgeOverlay ? nudgeOverlay.querySelector('[data-nudge-close]') : null;
  var nudgeFindBtn = nudgeOverlay ? nudgeOverlay.querySelector('[data-nudge-find]') : null;
  var nudgeSkipBtn = nudgeOverlay ? nudgeOverlay.querySelector('[data-nudge-skip]') : null;

  function closeNudge() {
    if (nudgeOverlay) nudgeOverlay.classList.add('hidden');
  }
  if (nudgeCloseBtn) nudgeCloseBtn.addEventListener('click', closeNudge);

  if (nudgeFindBtn) {
    nudgeFindBtn.addEventListener('click', function() {
      closeNudge();
      // Scroll finder into view (always visible now)
      var finderEl = pdp.querySelector('.hp-pdp__finder');
      if (finderEl) finderEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  if (nudgeSkipBtn) {
    nudgeSkipBtn.addEventListener('click', function() {
      closeNudge();
      vehicleNudgeSkipped = true;
      // Re-trigger the submit
      if (form) form.requestSubmit();
    });
  }

  // Close nudge on overlay click (outside modal)
  if (nudgeOverlay) {
    nudgeOverlay.addEventListener('click', function(e) {
      if (e.target === nudgeOverlay) closeNudge();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !nudgeOverlay.classList.contains('hidden')) closeNudge();
    });
  }

  function hasVehicleData() {
    try {
      var v = JSON.parse(localStorage.getItem('hp_selected_vehicle'));
      return v && v.year && v.make && v.model;
    } catch (e) { return false; }
  }

  if (form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      
      if (!selectedSize) {
        alert('Please select a size');
        return;
      }

      // Stock guard (belt-and-suspenders; the button is already disabled when OOS)
      if (!isAvailable(selectedSize, selectedColor)) {
        alert('Sorry, that size and color is out of stock. Please choose another color or size.');
        return;
      }

      // Soft nudge: if no vehicle data and user hasn't skipped, show modal
      if (!hasVehicleData() && !vehicleNudgeSkipped && nudgeOverlay) {
        nudgeOverlay.classList.remove('hidden');
        return;
      }
      
      var btnText = addBtn ? addBtn.querySelector('[data-add-to-cart-text]') : null;
      if (!btnText || !addBtn) return;
      
      var orig = btnText.textContent;
      addBtn.disabled = true;
      btnText.textContent = 'Adding...';
      
      // Populate YMM line item properties from saved vehicle
      try {
        var savedVehicle = JSON.parse(localStorage.getItem('hp_selected_vehicle'));
        
        var propYear = form.querySelector('[data-prop-vehicle-year]');
        var propMake = form.querySelector('[data-prop-vehicle-make]');
        var propModel = form.querySelector('[data-prop-vehicle-model]');
        var propSize = form.querySelector('[data-prop-vehicle-size]');
        var propFields = [propYear, propMake, propModel, propSize];
        
        if (savedVehicle && savedVehicle.year && savedVehicle.make && savedVehicle.model) {
          // Enable inputs so they're included in FormData
          propFields.forEach(function(el) { if (el) el.disabled = false; });
          if (propYear) propYear.value = savedVehicle.year;
          if (propMake) propMake.value = savedVehicle.make;
          if (propModel) propModel.value = savedVehicle.model;
          if (propSize && savedVehicle.sizeCode) {
            var sizeNames = window.HailProtector && window.HailProtector.sizeNames;
            propSize.value = sizeNames ? (sizeNames[savedVehicle.sizeCode] || savedVehicle.sizeCode) : savedVehicle.sizeCode;
          }
        } else {
          // No vehicle selected — disable inputs so they're excluded from FormData
          propFields.forEach(function(el) { if (el) el.disabled = true; });
        }
      } catch (e) {}
      
      // Use preserved clean fetch — apps (SlideCart, automatic-discount) hijack window.fetch
      // and drop line item properties. __hpFetch is saved before apps load in theme.liquid.
      var cleanFetch = window.__hpFetch || window.fetch;
      cleanFetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form)
      })
      .then(function(res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error();
        }
      })
      .then(function(item) {
        btnText.textContent = 'Added!';

        // Fetch cart and open drawer
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
            var drawer = document.querySelector('cart-drawer');
            if (drawer && drawer.open) drawer.open();
          });
      })
      .catch(function() {
        btnText.textContent = 'Error';
      })
      .finally(function() {
        setTimeout(function() {
          btnText.textContent = orig;
          addBtn.disabled = false;
        }, 2000);
      });
    });
  }
  
  // ========================================
  // INITIALIZE
  // ========================================
  function initialize() {
    var urlParams = new URLSearchParams(window.location.search);
    
    // Priority 1: ?variant=ID
    var urlVariantId = urlParams.get('variant');
    if (urlVariantId && variants) {
      var variant = variants.selectById(urlVariantId, false);
      if (variant) {
        var parsed = variants.parseVariant(variant);
        if (parsed) {
          setActiveSystem(parsed.system, false);
          selectedColor = parsed.color;
          updateColorUI();
          selectSize(parsed.size, false);
          if (gallery) {
            gallery.filter(parsed.system, parsed.color, variant);
          }
        }
        return;
      }
    }
    
    // Priority 2: ?system=
    var urlSystem = urlParams.get('system');
    if (urlSystem === 'guardian' || urlSystem === 'sentinel') {
      setActiveSystem(urlSystem, false);
    }
    
    // Initialize defaults
    updateSizePricesForColor(selectedColor);

    // Paint initial stock state (dormant while everything is available)
    refreshColorAvailability();
    refreshSizeAvailability();

    if (gallery) {
      gallery.filter(activeSystem, selectedColor);
    }
  }
  
  initialize();
});
